import { Button } from '@hologram-dimension/button';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { Tab } from '@hologram-dimension/tab';
import useInvoices from 'api/billing/useInvoices';
import { clsx } from 'clsx/lite';
import sharedPageStyles from 'pages/sharedPage.module.css';
import { useCallback, useMemo, type FC, type ReactNode } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { AccountViews, type AccountView } from 'types/common';
import useOrgUrl from 'utils/legacy/useOrgUrl';
import InvoicesView from 'views/Account/Invoices/Invoices';
import SettingsView from 'views/Account/Settings/Settings';
import styles from './Account.module.css';

interface AccountPageProps {
  className?: string;
  viewKey: AccountView;
  onNavigate: (url: string) => void;
  billingBaseUrl?: string;
  legacyInvoices?: ReactNode;

  // Address related to remove
  updateBillingAddress: (updatedAddress: any) => any;
  billingAddress: any;
  isBillingAddressSet: boolean;
}

// This inner wrapper needed to make sure SWR config is wrapped
const AccountPage: FC<AccountPageProps> = ({
  className,
  viewKey,
  onNavigate,
  billingBaseUrl,
  legacyInvoices,
  updateBillingAddress,
  billingAddress,
  isBillingAddressSet,
}) => {
  const organizationId = useOrganization();
  const { isLoading, error } = useInvoices({ organizationId });

  const url = useOrgUrl({ baseUrl: billingBaseUrl!, orgId: organizationId });
  const handleTabClick = useCallback(
    (view: AccountView) => {
      onNavigate(view === 'invoices' ? (url ?? '') : `${url}/${view}`);
    },
    [onNavigate, url]
  );

  // TOOD: Temporary workaround to just putting the "content" into an internal component
  const content = useMemo(() => {
    // TODO: As we add more views/tabs we should not show loaders except on what's required in each page.
    if (isLoading) {
      return <Loader className={styles.loader}>Loading account details...</Loader>;
    }

    if (error) {
      // typically when user session times out
      return (
        <PanelNotification
          variant="error"
          label="We weren’t able to load account details"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          You can try reloading the page.
        </PanelNotification>
      );
    }

    return (
      <>
        {/* TODO: Refactor to use BorderlessPanel, this gets us better scrolling AND the header stays pinned.
                May also prevent problems with the view jumping as scrollbars appear and disappear */}
        <div className={styles.headerWrapper}>
          <div className={styles.header}>Billing account</div>
          <hr className={styles.divider} />
          <div className={sharedPageStyles.tabWrapper}>
            {Object.entries(AccountViews)
              .filter(
                () =>
                  // TODO: Refactor to use a map of views to roles/feature flags (AI suggested this, I kind of like it)
                  true
              )
              .map(([view, viewName]) => (
                <Tab
                  id={`simdetails-tab-${view}`}
                  key={view}
                  selected={view === viewKey}
                  onClick={() => handleTabClick(view as AccountView)}
                >
                  {viewName}
                </Tab>
              ))}
          </div>
        </div>
        <div className={sharedPageStyles.viewsWrapper}>
          <div className={styles.viewContent}>
            {viewKey === 'invoices' && <InvoicesView legacyInvoices={legacyInvoices} />}
            {viewKey === 'settings' && (
              <SettingsView
                billingAddress={billingAddress}
                updateBillingAddress={updateBillingAddress}
                isBillingAddressSet={isBillingAddressSet}
              />
            )}
          </div>
        </div>
      </>
    );
  }, [
    isLoading,
    error,
    viewKey,
    legacyInvoices,
    billingAddress,
    updateBillingAddress,
    isBillingAddressSet,
    handleTabClick,
  ]);

  return (
    <div className={clsx(sharedPageStyles.pageWrapper, styles.wrapper, className)}>{content}</div>
  );
};

export default AccountPage;
